import { useReactiveVar } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import viewStateVar from 'src/store/viewState';

interface FilterChip {
  choice: string;
  category: string;
  chip: string;
}

interface FilterChipActions {
  handleDeleteChip: (index: number) => void;
  handleClearAll: () => void;
}

/**
 * Shared chip-delete / clear-all logic for the sticky search/filter bar.
 * Ported byte-for-byte from the PushedContents inline handlers so Library and
 * Gallery behave identically.
 */
export function useFilterChipActions(): FilterChipActions {
  const navigate = useNavigate();
  const { filterChips } = useReactiveVar(viewStateVar);
  const chips = filterChips as FilterChip[];

  const handleDeleteChip = (index: number): void => {
    const newChips = [...chips];
    newChips.splice(index, 1);
    const newFilters = newChips.map((c) => `${c.category}:${c.choice}`);
    viewStateVar({
      ...viewStateVar(),
      filterChips: newChips,
      filterCategoryExclude: newChips.map((c) => c.category),
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      pagination: { ...viewStateVar().pagination, cursor: null },
      filter: { ...viewStateVar().filter, choices: [] },
    });
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: newFilters,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  const handleClearAll = (): void => {
    viewStateVar({
      ...viewStateVar(),
      filterChips: [],
      filterCategoryExclude: [],
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      pagination: { ...viewStateVar().pagination, cursor: null },
      filter: {
        ...viewStateVar().filter,
        category: { id: '', name: '', label: '' },
        choices: [],
      },
    });
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: [],
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
  };

  return { handleDeleteChip, handleClearAll };
}
